import React, { useState, useEffect } from "react";
import {
  Paper,
  TextInput,
  Textarea,
  Button,
  Stack,
  Switch,
  SimpleGrid,
  LoadingOverlay,
  Title,
  Divider,
  Group,
  Text,
  Box,
  Image,
  ActionIcon,
  Center,
  BackgroundImage,
} from "@mantine/core";
import {
  IconDeviceFloppy,
  IconTrash,
  IconPhoto,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { useNotification } from "../../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../../config/baseUrls";
import { uploadFetch } from "../../../utils/directUpload";

const BASE_URL = `${API_BASE_URL}/shop-owner`;
const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("shopToken")}`,
  "Content-Type": "application/json",
});

const ImageUploadComponent = ({ onUpload, existingUrl, className }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(existingUrl);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        const res = await uploadFetch(
          `${API_BASE_URL}/auth/uploadImage`,
          {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ image: base64, name: file.name }),
          },
        );
        const data = await res.json();
        if (data.url) {
          setPreview(data.url);
          onUpload(data.url);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setUploading(false);
      }
    };
  };

  return (
    <Box className={className}>
      {preview ? (
        <Box
          pos="relative"
          w="100%"
          h={160}
          bg="gray.1"
          style={{ borderRadius: 8, overflow: "hidden" }}
        >
          <Image src={preview} h="100%" w="100%" fit="cover" />
          <ActionIcon
            color="red"
            variant="filled"
            pos="absolute"
            bottom={10}
            right={10}
            onClick={() => {
              setPreview(null);
              onUpload("");
            }}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Box>
      ) : (
        <Button
          component="label"
          variant="outline"
          color="gray"
          fullWidth
          h={120}
          style={{ borderStyle: "dashed" }}
          loading={uploading}
        >
          <Stack gap={0} align="center">
            <IconUpload size={24} />
            <Text size="sm" mt={4}>
              Upload Image
            </Text>
          </Stack>
          <input type="file" hidden accept="image/*" onChange={handleFile} />
        </Button>
      )}
    </Box>
  );
};

export default function ShopSettingsForm({ shop, onUpdate }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    bannerImageUrl: "",
    logoUrl: "",
    isActive: true,
    socialLinks: { instagram: "", facebook: "", twitter: "", website: "" },
  });
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    if (shop) {
      setFormData({
        name: shop.name || "",
        description: shop.description || "",
        bannerImageUrl: shop.bannerImageUrl || "",
        logoUrl: shop.logoUrl || "",
        isActive: shop.isActive,
        socialLinks: shop.socialLinks || {
          instagram: "",
          facebook: "",
          twitter: "",
          website: "",
        },
      });
    }
  }, [shop]);

  const handleSave = async () => {
    if (!formData.name) return showError("Error", "Shop Name is required");

    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/shops/${shop.id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(formData),
      });
      const data = await res.json();

      if (data.success) {
        showSuccess("Saved", "Shop settings updated successfully");
        if (onUpdate) onUpdate();
      } else {
        showError("Error", data.error || "Update failed");
      }
    } catch (e) {
      showError("Error", "Network error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="lg" maxW={900}>
      <Paper p="xl" radius="md" withBorder pos="relative">
        <LoadingOverlay visible={saving} overlayProps={{ blur: 2 }} />

        <Stack gap="lg">
          {/* SECTION 1: GENERAL */}
          <div>
            <Title order={4} mb="xs">
              General Information
            </Title>
            <Text c="dimmed" size="sm" mb="md">
              Basic details about your shop visible to customers.
            </Text>
            <Stack gap="sm">
              <TextInput
                label="Shop Name"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              <Textarea
                label="Description"
                minRows={3}
                description="Brief description shown on your shopfront"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Stack>
          </div>

          <Divider />

          {/* SECTION 2: BRANDING (With Existing Image Preview) */}
          <div>
            <Title order={4} mb="xs">
              Branding
            </Title>
            <Text c="dimmed" size="sm" mb="md">
              Upload your shop logo and banner image.
            </Text>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
              {/* --- LOGO COLUMN --- */}
              <Box>
                <Text fw={500} size="sm" mb={4}>
                  Logo (Square)
                </Text>

                {formData.logoUrl ? (
                  // STATE A: SHOW EXISTING IMAGE
                  <Box
                    pos="relative"
                    w={160}
                    h={160}
                    style={{
                      borderRadius: 8,
                      overflow: "hidden",
                      border: "1px solid #eee",
                    }}
                  >
                    <Image
                      src={formData.logoUrl}
                      w="100%"
                      h="100%"
                      fit="cover"
                    />
                    <ActionIcon
                      color="red"
                      variant="filled"
                      pos="absolute"
                      top={5}
                      right={5}
                      onClick={() => setFormData({ ...formData, logoUrl: "" })}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Box>
                ) : (
                  // STATE B: SHOW UPLOADER
                  <Box h={160} mb="xs">
                    <ImageUploadComponent
                      onUpload={(url) =>
                        setFormData({ ...formData, logoUrl: url })
                      }
                    />
                  </Box>
                )}

                {/* Fallback Input */}
                <TextInput
                  size="xs"
                  mt="xs"
                  placeholder="Or paste image URL"
                  value={formData.logoUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, logoUrl: e.target.value })
                  }
                  leftSection={<IconPhoto size={12} />}
                />
              </Box>

              {/* --- BANNER COLUMN --- */}
              <Box>
                <Text fw={500} size="sm" mb={4}>
                  Banner (Wide)
                </Text>

                {formData.bannerImageUrl ? (
                  // STATE A: SHOW EXISTING IMAGE
                  <Box
                    pos="relative"
                    h={160}
                    w="100%"
                    style={{
                      borderRadius: 8,
                      overflow: "hidden",
                      border: "1px solid #eee",
                    }}
                  >
                    <BackgroundImage
                      src={formData.bannerImageUrl}
                      h="100%"
                      w="100%"
                    >
                      <Box
                        p="xs"
                        display="flex"
                        style={{ justifyContent: "flex-end" }}
                      >
                        <ActionIcon
                          color="red"
                          variant="filled"
                          onClick={() =>
                            setFormData({ ...formData, bannerImageUrl: "" })
                          }
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Box>
                    </BackgroundImage>
                  </Box>
                ) : (
                  // STATE B: SHOW UPLOADER
                  <Box h={160} mb="xs">
                    <ImageUploadComponent
                      onUpload={(url) =>
                        setFormData({ ...formData, bannerImageUrl: url })
                      }
                    />
                  </Box>
                )}

                {/* Fallback Input */}
                <TextInput
                  size="xs"
                  mt="xs"
                  placeholder="Or paste image URL"
                  value={formData.bannerImageUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, bannerImageUrl: e.target.value })
                  }
                  leftSection={<IconPhoto size={12} />}
                />
              </Box>
            </SimpleGrid>
          </div>

          <Divider />

          {/* SECTION 3: SOCIAL LINKS */}
          <div>
            <Title order={4} mb="xs">
              Social Media
            </Title>
            <SimpleGrid cols={{ base: 1, sm: 2 }} mt="md">
              <TextInput
                label="Instagram"
                placeholder="https://instagram.com/..."
                value={formData.socialLinks?.instagram || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    socialLinks: {
                      ...formData.socialLinks,
                      instagram: e.target.value,
                    },
                  })
                }
              />
              <TextInput
                label="Facebook"
                placeholder="https://facebook.com/..."
                value={formData.socialLinks?.facebook || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    socialLinks: {
                      ...formData.socialLinks,
                      facebook: e.target.value,
                    },
                  })
                }
              />
              <TextInput
                label="Twitter / X"
                placeholder="https://twitter.com/..."
                value={formData.socialLinks?.twitter || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    socialLinks: {
                      ...formData.socialLinks,
                      twitter: e.target.value,
                    },
                  })
                }
              />
              <TextInput
                label="Website"
                placeholder="https://myshop.com"
                value={formData.socialLinks?.website || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    socialLinks: {
                      ...formData.socialLinks,
                      website: e.target.value,
                    },
                  })
                }
              />
            </SimpleGrid>
          </div>

          <Divider />

          {/* SECTION 4: SETTINGS */}
          <Paper withBorder p="md" bg="gray.0">
            <Group justify="space-between">
              <div>
                <Text fw={600}>Shop Status</Text>
                <Text size="sm" c="dimmed">
                  When disabled, your shop is hidden from customers.
                </Text>
              </div>
              <Switch
                size="lg"
                onLabel="ON"
                offLabel="OFF"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    isActive: e.currentTarget.checked,
                  })
                }
              />
            </Group>
          </Paper>

          <Button
            size="md"
            mt="md"
            fullWidth
            onClick={handleSave}
            loading={saving}
            leftSection={<IconDeviceFloppy size={20} />}
            className="btn-ota-primary"
          >
            Save Changes
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
